import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { 
  RefreshCw, AlertTriangle, AlertCircle, Info, CheckCircle2, 
  ChevronDown, ChevronUp, Database, Search, FileText, Download,
  Package, Barcode, DollarSign, Tag, TrendingDown
} from "lucide-react";
import { toast } from "sonner";
import { PriceMismatchPanel } from "@/components/dataquality/PriceMismatchPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const severityConfig = {
  error: { color: "bg-red-100 text-red-800 border-red-200", icon: AlertCircle, iconColor: "text-red-600" },
  warning: { color: "bg-amber-100 text-amber-800 border-amber-200", icon: AlertTriangle, iconColor: "text-amber-600" },
  info: { color: "bg-blue-100 text-blue-800 border-blue-200", icon: Info, iconColor: "text-blue-600" }
};

const checkIcons = {
  "plu-matches-supplier-plu": Tag,
  "duplicate-supplier-plus": Tag,
  "duplicate-barcodes": Barcode,
  "missing-barcodes": Barcode,
  "negative-stock": TrendingDown,
  "zero-cost-with-stock": DollarSign,
  "zero-selling-price": DollarSign,
  "missing-category": Package,
  "price-mismatch": DollarSign
};

export default function DataQualityCheck() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedCheck, setExpandedCheck] = useState(null);
  const [checkDetails, setCheckDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState({});

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/data-quality/summary`);
      setSummary(response.data);
    } catch (error) {
      console.error("Error fetching data quality summary:", error);
      toast.error("Failed to fetch data quality summary");
    } finally {
      setLoading(false);
    }
  };

  const fetchCheckDetails = async (checkId) => {
    if (checkDetails[checkId]) {
      // Already loaded, just toggle
      setExpandedCheck(expandedCheck === checkId ? null : checkId);
      return;
    }

    setLoadingDetails({ ...loadingDetails, [checkId]: true });
    try {
      const response = await axios.get(`${API}/data-quality/${checkId}`);
      setCheckDetails({ ...checkDetails, [checkId]: response.data });
      setExpandedCheck(checkId);
    } catch (error) {
      console.error(`Error fetching details for ${checkId}:`, error);
      toast.error("Failed to fetch check details");
    } finally {
      setLoadingDetails({ ...loadingDetails, [checkId]: false });
    }
  };

  const exportToCSV = (checkId, data) => {
    if (!data || !data.items || data.items.length === 0) {
      toast.error("No data to export");
      return;
    }

    let csvContent = "";
    const items = data.items;

    // Handle different data structures
    if (checkId === "duplicate-supplier-plus" || checkId === "duplicate-barcodes") {
      csvContent = "Duplicate Value,Count,Products\n";
      items.forEach(item => {
        const products = item.products.map(p => `${p.plu_code}: ${p.name}`).join(" | ");
        csvContent += `"${item._id}",${item.count},"${products}"\n`;
      });
    } else {
      // Standard product list
      const headers = Object.keys(items[0]).filter(k => k !== "id");
      csvContent = headers.join(",") + "\n";
      items.forEach(item => {
        const row = headers.map(h => `"${item[h] || ''}"`).join(",");
        csvContent += row + "\n";
      });
    }

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `data-quality-${checkId}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("CSV exported successfully");
  };

  useEffect(() => {
    fetchSummary();
  }, []);

  const totalIssues = summary?.checks?.reduce((sum, c) => sum + (c.severity !== 'info' ? c.count : 0), 0) || 0;
  const errorCount = summary?.checks?.filter(c => c.severity === 'error').reduce((sum, c) => sum + c.count, 0) || 0;
  const warningCount = summary?.checks?.filter(c => c.severity === 'warning').reduce((sum, c) => sum + c.count, 0) || 0;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="data-quality-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Database className="h-8 w-8 text-blue-600" />
            Data Quality Check
          </h1>
          <p className="text-gray-600 mt-1">Identify and fix data integrity issues in your product database</p>
        </div>
        <Button onClick={fetchSummary} disabled={loading} className="gap-2">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-4xl font-bold text-gray-900">{summary.total_products.toLocaleString()}</div>
                <div className="text-sm text-gray-500 mt-1">Total Products</div>
              </div>
            </CardContent>
          </Card>
          <Card className={errorCount > 0 ? "border-red-300 bg-red-50" : ""}>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className={`text-4xl font-bold ${errorCount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {errorCount}
                </div>
                <div className="text-sm text-gray-500 mt-1 flex items-center justify-center gap-1">
                  <AlertCircle className="h-4 w-4 text-red-500" />
                  Critical Issues
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={warningCount > 0 ? "border-amber-300 bg-amber-50" : ""}>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className={`text-4xl font-bold ${warningCount > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                  {warningCount}
                </div>
                <div className="text-sm text-gray-500 mt-1 flex items-center justify-center gap-1">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  Warnings
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600">
                  {summary.checks?.length || 0}
                </div>
                <div className="text-sm text-gray-500 mt-1 flex items-center justify-center gap-1">
                  <CheckCircle2 className="h-4 w-4 text-blue-500" />
                  Checks Run
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Timestamp */}
      {summary?.generated_at && (
        <div className="text-sm text-gray-500 mb-4">
          Last checked: {new Date(summary.generated_at).toLocaleString()}
        </div>
      )}

      {/* Check Results */}
      <div className="space-y-3">
        {summary?.checks?.map((check) => {
          const config = severityConfig[check.severity];
          const IconComponent = checkIcons[check.id] || FileText;
          const isExpanded = expandedCheck === check.id;
          const details = checkDetails[check.id];
          const isLoadingDetails = loadingDetails[check.id];

          return (
            <Card key={check.id} className={`${check.count > 0 && check.severity !== 'info' ? config.color : ''} transition-all`}>
              <CardHeader className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <IconComponent className={`h-5 w-5 ${check.count > 0 ? config.iconColor : 'text-gray-400'}`} />
                    <div>
                      <CardTitle className="text-base font-semibold">{check.name}</CardTitle>
                      <p className="text-sm text-gray-600">{check.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant={check.count > 0 && check.severity !== 'info' ? "destructive" : "secondary"} className="text-lg px-3 py-1">
                      {check.count.toLocaleString()}
                    </Badge>
                    {check.count > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchCheckDetails(check.id)}
                        data-testid={`dq-view-${check.id}`}
                        disabled={isLoadingDetails}
                        className="gap-1"
                      >
                        {isLoadingDetails ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : isExpanded ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                        {isExpanded ? "Hide" : "View"}
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>

              {/* Expanded Details */}
              {isExpanded && details && check.id === "price-mismatch" && (
                <CardContent className="border-t bg-white">
                  <PriceMismatchPanel
                    details={details}
                    onFixed={() => { setCheckDetails({ ...checkDetails, [check.id]: null }); setExpandedCheck(null); fetchSummary(); }}
                  />
                </CardContent>
              )}
              {isExpanded && details && check.id !== "price-mismatch" && (
                <CardContent className="border-t bg-white">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-sm text-gray-600">
                      Showing {details.showing || details.items?.length || 0} of {details.count} items
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => exportToCSV(check.id, details)}
                      className="gap-1"
                    >
                      <Download className="h-4 w-4" />
                      Export CSV
                    </Button>
                  </div>

                  {/* Duplicate items have different structure */}
                  {(check.id === "duplicate-supplier-plus" || check.id === "duplicate-barcodes") ? (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {details.items?.map((item, idx) => (
                        <div key={idx} className="border rounded-lg p-3 bg-gray-50">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline" className="font-mono">
                              {check.id === "duplicate-barcodes" ? "Barcode" : "Supplier PLU"}: {item._id}
                            </Badge>
                            <Badge variant="secondary">{item.count} products</Badge>
                          </div>
                          <div className="space-y-1">
                            {item.products?.slice(0, 10).map((product, pIdx) => (
                              <div key={pIdx} className="text-sm flex items-center gap-2">
                                <span className="font-mono text-blue-600">{product.plu_code}</span>
                                <span className="text-gray-600">-</span>
                                <span>{product.name}</span>
                              </div>
                            ))}
                            {item.products?.length > 10 && (
                              <div className="text-sm text-gray-500 italic">
                                ... and {item.products.length - 10} more
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="max-h-96 overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-24">PLU</TableHead>
                            <TableHead>Name</TableHead>
                            {details.items?.[0]?.supplier_plu !== undefined && (
                              <TableHead className="w-32">Supplier PLU</TableHead>
                            )}
                            {details.items?.[0]?.current_stock !== undefined && (
                              <TableHead className="w-24 text-right">Stock</TableHead>
                            )}
                            {details.items?.[0]?.cost_price_excl_vat !== undefined && (
                              <TableHead className="w-28 text-right">Cost</TableHead>
                            )}
                            {details.items?.[0]?.selling_price_incl_vat !== undefined && (
                              <TableHead className="w-28 text-right">Sell Price</TableHead>
                            )}
                            <TableHead className="w-32">Category</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {details.items?.slice(0, 100).map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-mono text-blue-600">{item.plu_code}</TableCell>
                              <TableCell className="max-w-xs truncate">{item.name}</TableCell>
                              {item.supplier_plu !== undefined && (
                                <TableCell className="font-mono text-gray-600">{item.supplier_plu || '-'}</TableCell>
                              )}
                              {item.current_stock !== undefined && (
                                <TableCell className={`text-right font-mono ${item.current_stock < 0 ? 'text-red-600 font-bold' : ''}`}>
                                  {item.current_stock}
                                </TableCell>
                              )}
                              {item.cost_price_excl_vat !== undefined && (
                                <TableCell className="text-right font-mono">
                                  R {(item.cost_price_excl_vat || 0).toFixed(2)}
                                </TableCell>
                              )}
                              {item.selling_price_incl_vat !== undefined && (
                                <TableCell className="text-right font-mono">
                                  R {(item.selling_price_incl_vat || 0).toFixed(2)}
                                </TableCell>
                              )}
                              <TableCell className="text-gray-600">{item.category || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {details.items?.length > 100 && (
                        <div className="text-center py-3 text-sm text-gray-500">
                          Showing first 100 items. Export to CSV to see all {details.count} items.
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {/* No Issues Message */}
      {summary && totalIssues === 0 && (
        <Card className="mt-6 bg-green-50 border-green-200">
          <CardContent className="pt-6">
            <div className="text-center">
              <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto mb-2" />
              <h3 className="text-lg font-semibold text-green-800">All Clear!</h3>
              <p className="text-green-600">No critical issues or warnings found in your data.</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
